// Chooser shown before character creation: Quick Create vs Custom Create.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Inline lucide icons (static, trusted SVGs; inherit currentColor).
const zapIcon = html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="32" height="32"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" /></svg>`;
const slidersIcon = html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="32" height="32"><path d="M10 5H3" /><path d="M12 19H3" /><path d="M14 3v4" /><path d="M16 17v4" /><path d="M21 12h-9" /><path d="M21 19h-5" /><path d="M21 5h-7" /><path d="M8 10v4" /><path d="M8 12H3" /></svg>`;

class ChooseCharacterModeView extends BaseComponent {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .choose-wrap {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      padding: 2rem;
      max-width: 720px;
      margin: 0 auto;
      width: 100%;
    }

    @media (max-width: 540px) {
      .choose-wrap {
        grid-template-columns: 1fr;
      }
    }

    .mode-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      padding: 1.5rem 1.25rem;
      border: 2px solid #dee2e6;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.06);
      cursor: pointer;
      text-align: center;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        background-color 0.15s ease;
    }

    .mode-card:hover {
      border-color: #0d6efd;
      background: rgba(13, 110, 253, 0.06);
      box-shadow: 0 4px 16px rgba(13, 110, 253, 0.15);
    }

    .mode-card:focus-visible {
      outline: 2px solid #0d6efd;
      outline-offset: 2px;
    }

    .mode-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: rgba(13, 110, 253, 0.1);
      color: #0d6efd;
    }

    .mode-title {
      font-size: 1.15rem;
      font-weight: 600;
      color: #212529;
    }

    .mode-description {
      color: #6c757d;
      font-size: 0.85rem;
      line-height: 1.4;
    }
  `;

  /**
   * Navigate to the target path while forwarding any current query params
   * (e.g. skipRestore, returnTo) so downstream views retain context.
   * @param {string} path
   */
  _go(path) {
    const query = router.getQuery();
    const qs = query.toString();
    router.navigate(qs ? `${path}?${qs}` : path);
  }

  render() {
    return html`
      <div class="choose-wrap">
        <button
          type="button"
          class="mode-card"
          @click="${() => this._go('/characters/new/quick')}"
          data-testid="quick-create-button"
        >
          <span class="mode-icon">${zapIcon}</span>
          <span class="mode-title">Quick Create</span>
          <span class="mode-description">
            Pick a class, species, and background. Name your character and choose a token — everything else is filled in
            for you.
          </span>
        </button>
        <button
          type="button"
          class="mode-card"
          @click="${() => this._go('/characters/new/custom')}"
          data-testid="custom-create-button"
        >
          <span class="mode-icon">${slidersIcon}</span>
          <span class="mode-title">Custom Create</span>
          <span class="mode-description">
            Walk through every step: ability scores, skills, equipment, alignment, and more. Full control over your
            character.
          </span>
        </button>
      </div>
    `;
  }
}

customElements.define('choose-character-mode-view', ChooseCharacterModeView);
